import { ReactNode } from "react";
import { Box, MenuItem } from "@mui/material";
import { BuilderTextField } from "./builderUi";
import { ucFirstWord } from "../../utils/textUtils";
import { BANK_REASON_CODES, BankFactsValues, BankReasonCode } from "../../types/evidenceBankCase";

// The facts only the bank's portal knows. The details step and the evidence page's "Edit details"
// both print it, so a field added here reaches both.
export default function BankFactsForm({ values, errors, disabled, onChange, children }: {
  values: BankFactsValues;
  /** Empty until the agent has tried to send the form. */
  errors: Partial<Record<keyof BankFactsValues, string>>;
  disabled: boolean;
  onChange: (values: BankFactsValues) => void;
  /** Further fields of the caller's, laid out after these. */
  children?: ReactNode;
}) {
  const field = (key: keyof BankFactsValues) => ({
    disabled,
    value: values[key],
    error: Boolean(errors[key]),
    helperText: errors[key],
    onChange: (event: { target: { value: string } }) => onChange({ ...values, [key]: event.target.value }),
  });

  return (
    <Box sx={{ display: "grid", gap: "14px", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" } }}>
      <BuilderTextField label="Bank" {...field("bankName")} />
      <BuilderTextField label="Dispute ID" {...field("disputeReference")} />
      <BuilderTextField label="Merchant ID" placeholder="MID, as the bank's portal shows it" {...field("merchantId")} />
      <BuilderTextField
        label="Reason"
        select
        {...field("reasonCode")}
        onChange={(event) => onChange({ ...values, reasonCode: event.target.value as BankReasonCode })}
      >
        {BANK_REASON_CODES.map((code) => <MenuItem key={code} value={code}>{ucFirstWord(code)}</MenuItem>)}
      </BuilderTextField>
      <BuilderTextField label="Reason as the bank words it" {...field("reasonText")} />
      <BuilderTextField label="Disputed amount" {...field("amount")} slotProps={{ htmlInput: { inputMode: "decimal" } }} />
      <BuilderTextField label="Currency" {...field("currency")} slotProps={{ htmlInput: { maxLength: 3 } }} />
      <BuilderTextField label="Disputed on" type="date" {...field("disputedOn")} />
      <BuilderTextField label="Respond by" type="date" {...field("respondBy")} />
      {children}
    </Box>
  );
}
